import { createFileRoute } from '@tanstack/react-router'
import { Button, Empty } from 'regen-ui'
import { tempoModerato } from 'wagmi/chains'
import Activity from '~icons/lucide/activity'
import CircleAlert from '~icons/lucide/circle-alert'
import Wallet from '~icons/lucide/wallet'

import type { BalancesCard } from '#/routes/_app/-components/BalancesCard.js'
import { BalancesCard as BalancesCardComponent } from '#/routes/_app/-components/BalancesCard.js'

export const Route = createFileRoute('/design/')({
  component: Design,
})

function mockAsset(i: number, overrides: Partial<BalancesCard.Asset> = {}): BalancesCard.Asset {
  const names = [
    'PathUSD',
    'AlphaUSD',
    'BetaUSD',
    'ThetaUSD',
    'GammaUSD',
    'DeltaUSD',
    'EpsilonUSD',
    'ZetaUSD',
  ]
  const name = names[i % names.length]
  return {
    address: `0x20c000000000000000000000000000000000000${i}` as `0x${string}`,
    balance: '1000000000000',
    decimals: 6,
    fiat: { balance: '1000000', currency: 'USD' },
    name,
    symbol: name,
    ...overrides,
  }
}

const balanceScenarios = [
  {
    label: 'Empty — no tokens',
    assets: [] as BalancesCard.Asset[],
  },
  {
    label: 'Single token',
    assets: [mockAsset(0, { balance: '50000000', fiat: { balance: '50', currency: 'USD' } })],
  },
  {
    label: 'Two tokens — mixed values',
    assets: [
      mockAsset(0, { balance: '2500000000', fiat: { balance: '2500', currency: 'USD' } }),
      mockAsset(1, { balance: '150000', fiat: { balance: '0.15', currency: 'USD' } }),
    ],
  },
  {
    label: 'Four tokens (max visible)',
    assets: [
      mockAsset(0, { balance: '1000000000000', fiat: { balance: '1000000', currency: 'USD' } }),
      mockAsset(1, { balance: '500000000000', fiat: { balance: '500000', currency: 'USD' } }),
      mockAsset(2, { balance: '75000000', fiat: { balance: '75', currency: 'USD' } }),
      mockAsset(3, { balance: '1250000', fiat: { balance: '1.25', currency: 'USD' } }),
    ],
  },
  {
    label: 'Unrecognized tokens — no icon',
    assets: [
      mockAsset(0, {
        address: '0xdead000000000000000000000000000000000001' as `0x${string}`,
        balance: '5000000000',
        fiat: undefined,
        name: 'Unknown Token',
        symbol: 'UNK',
      }),
      mockAsset(1, {
        address: '0xdead000000000000000000000000000000000002' as `0x${string}`,
        balance: '123456789',
        fiat: undefined,
        name: 'Wrapped ETH',
        symbol: 'WETH',
      }),
      mockAsset(2, { balance: '750000000', fiat: { balance: '750', currency: 'USD' } }),
    ],
  },
  {
    label: 'Many tokens — overflow (shows first 4)',
    assets: Array.from({ length: 12 }, (_, i) =>
      mockAsset(i, {
        balance: String(BigInt(Math.floor(Math.random() * 10_000_000)) * 1000000n),
        fiat: { balance: String(Math.floor(Math.random() * 10_000_000)), currency: 'USD' },
      }),
    ),
  },
] as const

function Design() {
  return (
    <main className="min-w-0 flex-1 py-12">
      <div className="max-w-[960px] px-8 lg:px-12">
        <h1 className="text-heading-32 text-foreground">Components</h1>

        <section className="flex flex-col gap-6 pt-12" id="empty-state">
          <div className="flex flex-col gap-1">
            <h2 className="text-heading-20 text-foreground">Empty</h2>
            <p className="text-label-14 text-foreground-secondary">
              Centered state for empty lists, blocked screens, and recoverable errors.
            </p>
          </div>

          <div className="grid max-w-[760px] gap-4 md:grid-cols-2">
            <div className="rounded-body border border-border bg-surface">
              <Empty icon={<Activity />} title="No activity yet">
                Transactions, deposits, swaps, and app authorizations will appear here.
              </Empty>
            </div>
            <div className="rounded-body border border-border bg-surface">
              <Empty
                action={
                  <Button size="small" variant="secondary">
                    Retry
                  </Button>
                }
                icon={<CircleAlert />}
                title="Couldn't load authorized apps"
              >
                Refresh this list to check the latest app permissions.
              </Empty>
            </div>
            <div className="rounded-body border border-border bg-surface md:col-span-2">
              <Empty icon={<Wallet />} title="No balances yet">
                Deposit funds to start using your wallet.
              </Empty>
            </div>
          </div>
        </section>

        <section className="mt-12" id="balances-card">
          <h2 className="text-heading-20 text-foreground">BalancesCard</h2>
          <p className="mt-1 text-label-14 text-foreground-secondary">
            Token balance list with fiat/crypto toggle per row.
          </p>

          <div className="mt-6 flex flex-col gap-6">
            {balanceScenarios.map((scenario) => (
              <div key={scenario.label}>
                <p className="mb-2 text-label-13 text-foreground-secondary">{scenario.label}</p>
                <div className="max-w-[600px] [&>*]:min-h-[240px]">
                  <BalancesCardComponent assets={scenario.assets} chainId={tempoModerato.id} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  )
}
